import {Component, ElementRef, Input, OnChanges, OnInit, signal, SimpleChanges, viewChildren} from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { select, Store } from '@ngrx/store';
import {firstValueFrom, Observable} from 'rxjs';
import { ShiftStatisticsStatus, ShiftWithDriverAndVehicleModel } from '../../../../shared/models/shift.model';
import { VehiclesService } from '../../../../data/vehicles/vehicles.service';
import { HardwareConfiguration, SensorType, VehicleStats } from '../../../../shared/models/vehicle.model';
import moment from 'moment';
import { ReportsService } from '../../../../data/reports/reports.service';
import { saveAs } from 'file-saver-es';
import { ConfigurationModel, FeatureFlagEnum } from '../../../../shared/models/configuration.model';
import { ToastService } from '../../../../shared/services/toast.service';
import { GpsSourcePipe } from '../../../../shared/formatting/gps-source.pipe';
import { MatDialog } from '@angular/material/dialog';
import { InsightsRoute, ShiftDetailRoute } from '../../../insights/insights-routing.module';
import { DeviceInfoService } from '../../../../data/device/device-info.service';
import { DeviceInfo } from '../../../../shared/models/device-info';
import { MainRoute } from '../../../../shared/models/angular-routing';
import { ImageModel } from '../../../../shared/models/image';
import { RouteAssignment } from '../../../../shared/models/route-assignment';
import { Observation } from '../../../../shared/models/observation';
import { ActionMenuItem } from '../../../../shared/models/action-menu-item.class';
import { ShiftMapDataService } from '../../services/shift-map-data.service';
import { HighlightedItemSource } from '../../../../shared/models/highlighted-item-update';
import { ActivatedRoute, Router } from '@angular/router';
import {ShiftsService} from '../../../../data/shifts/shifts.service';
import { ShiftDevices } from '../../../../data/websocket/model/message.event';
import { DatePipe } from '@angular/common';
import { MapStyles } from '../../../../configuration/map-styles';
import { MatButton } from '@angular/material/button';
import { FormattingModule } from '../../../../shared/formatting/formatting.module';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActionMenuItemComponent } from '../../../../shared/components/action-menu-item/action-menu-item.component';
import { ShiftMapInspectionDialogComponent } from '../shift-map-inspection-dialog/shift-map-inspection-dialog';
import { InspectionReport } from '../../../../shared/models/dvir';

@Component({
  selector: 'app-shift-map-panel',
  templateUrl: './shift-map-panel.component.html',
  styleUrls: ['./shift-map-panel.component.scss'],
  imports: [DatePipe, MatButton, FormattingModule, MatProgressSpinner, ActionMenuItemComponent],
})
export class ShiftMapPanelComponent implements OnInit, OnChanges {
  @Input() configuration: ConfigurationModel;
  @Input() shift: ShiftWithDriverAndVehicleModel;
  @Input() images: ImageModel[];
  @Input() observations: Observation[];
  @Input() collapseMenu?: boolean;
  @Input() assignments: RouteAssignment[];
  background: ThemePalette = undefined;
  sidenavOpen$?: Observable<boolean>;

  statsLoading = true;
  stats: VehicleStats;
  deviceInfo: DeviceInfo[];

  assignmentsLoading = true;

  highlightedObservationId: number;
  observationItems: ActionMenuItem[];
  assignmentItems: ActionMenuItem[];
  inspectionItems: ActionMenuItem[] = [];
  inspectionItemsLoading = signal(true);

  FeatureFlagEnum = FeatureFlagEnum;
  InsightsRoute = InsightsRoute;
  MainRoute = MainRoute;
  StatisticsStatus = ShiftStatisticsStatus;
  ShiftDevices = ShiftDevices;
  HardwareConfiguration = HardwareConfiguration;
  SensorType = SensorType;

  // Signal-based viewChildren query returns a Signal containing an array of ElementRefs
  private itemRefs = viewChildren('itemRef', {read: ElementRef});

  constructor(
    private store: Store<any>,
    private shiftMapManager: ShiftMapDataService,
    private vehicleService: VehiclesService,
    private shiftService: ShiftsService,
    private reportService: ReportsService,
    private deviceInfoService: DeviceInfoService,
    private gpsSourcePipe: GpsSourcePipe,
    private toast: ToastService,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.sidenavOpen$ = this.store.pipe(select(state => state.ui.sidenavOpen));

    this.loadStats(false);

    this.shiftMapManager.highlightedObservation$.subscribe(highlightedObservationUpdate => {
      this.highlightedObservationId = highlightedObservationUpdate.itemId;
      if (!!this.observations) {
        this.updateObservationItems();
      }
      this.selectAndScrollObservation(highlightedObservationUpdate.itemId);
    });

    // call both, but wait for both to be able to sort them by id so pre-trip displays earlier in list
    Promise.all([
      this.shift.startInspectionId > 0 ? this.reportService.getInspectionReport(this.shift.startInspectionId) : Promise.resolve(),
      this.shift.endInspectionId > 0 ? this.reportService.getInspectionReport(this.shift.endInspectionId) : Promise.resolve()
    ]).then(responses => {
      const [pre, post] = responses;
      if (pre && pre.data) {
        this.inspectionItems.push(
          this.inspectionReportToActionMenuItem(pre.data, true)
        );
      }
      if (post && post.data) {
        this.inspectionItems.push(
          this.inspectionReportToActionMenuItem(post.data, false)
        );
      }
    }).finally(() => {
      // sort them when ready, so pre-trip is above post-trip
      this.inspectionItems.sort((a, b) => +a.id - +b.id);
      this.inspectionItemsLoading.set(false);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const observations = changes['observations']?.currentValue as Observation[];
    if (!!observations) {
      this.updateObservationItems();
    }
    const assignments = changes['assignments']?.currentValue as RouteAssignment[];
    if (!!assignments) {
      this.updateAssignmentItems();
    }
  }

  getDuration() {
    const end = moment(this.shift.end);
    const start = moment(this.shift.start);
    return moment.duration(end.diff(start));
  }

  loadStats(loadShift: boolean = true) {
    if (this.shift != null) {
      this.statsLoading = true;
      // load shift with processing status, if needed
      const shiftPromise = loadShift ? this.shiftService.getShiftInfo(this.shift.id) : Promise.resolve(null);
      Promise.all([
        shiftPromise,
        this.vehicleService.getVehicleStats(this.shift.vehicle.id, this.shift.id),
        firstValueFrom(this.deviceInfoService.getDeviceInfoForShift(this.shift.id)),
      ])
        .then(responses => {
          const [shift, vehicleStats, deviceInfo] = responses;
          if (!!shift && !!shift.data) {
            this.shift = shift.data;
          }
          this.stats = vehicleStats.data;
          this.deviceInfo = deviceInfo.data;
        })
        .catch(message => {
          this.toast.long(message.error);
        })
        .finally(() => {
          this.statsLoading = false;
        });
    }
  }

  getGpsSource() {
    return this.stats && this.stats.gpsSources && this.stats.gpsSources.length > 0
      ? this.stats.gpsSources.map(source => this.gpsSourcePipe.transform(source)).join(', ')
      : 'N/A';
  }

  previewDVIR(preTrip: boolean) {
    const dialog = this.dialog.open(ShiftMapInspectionDialogComponent, {
      maxWidth: '80vw',
      maxHeight: '90vh',
      data: {
        shiftId: this.shift.id,
        preTrip,
      },
    });
    dialog
      .afterClosed()
      .toPromise()
      .then(result => {
        if (result) {
          this.downloadDVIR(preTrip);
        }
      });
  }

  private downloadDVIR(preTrip: boolean) {
    this.reportService
      .getDvirByShiftPDF(this.shift.id, preTrip)
      .then(response => {
        const blobType = response?.type ?? 'pdf';
        const mime = blobType.substring(0, blobType.indexOf(';')).toLowerCase();
        let ext = 'pdf';
        if (mime === 'text/html') {
          ext = 'html';
        }
        const blob = new Blob([response], { type: ext });
        const suffix = preTrip ? 'pre' : 'post';
        saveAs(blob, `Inspection_Report__id_${this.shift.id}_${suffix}.${ext}`);
      });
  }

  private updateAssignmentItems() {
    this.assignmentItems = this.assignments.map(a => this.assignmentToActionMenuItem(a));
  }

  private assignmentToActionMenuItem(assignment: RouteAssignment): ActionMenuItem {
    return new ActionMenuItem(
      assignment.id,
      'task',
      assignment.routeName,
      assignment.percentCompleted !== undefined ? `${(assignment.percentCompleted * 100).toFixed(1)}%` : '',
      '',
      null,
      null,
      () => false,
      null,
      null,
      () => this.router.navigate([ShiftDetailRoute.ASSIGNMENT, assignment.id], { relativeTo: this.activatedRoute }),
      null,
      null,
      []
    );
  }

  private updateObservationItems() {
    this.observationItems = this.observations.map(observation => this.observationToActionMenuItem(observation));
  }

  private observationToActionMenuItem(observation: Observation): ActionMenuItem {
    return new ActionMenuItem(
      observation.id,
      'stars',
      observation.observationType.title,
      this.datePipe.transform(observation.created, 'shortTime'),
      '',
      null,
      !!observation.observationType.group?.mapColor ? observation.observationType.group.mapColor : MapStyles.LIVE_COLOR,
      () => this.highlightedObservationId === observation.id,
      null,
      null,
      () => this.selectObservation(observation),
      null,
      null,
      []
    );
  }

  private selectObservation(observation: Observation) {
    this.shiftMapManager.sendHighlightedObservationUpdate(observation.id, HighlightedItemSource.PANEL);
  }

  private inspectionReportToActionMenuItem(i: InspectionReport, onStart = false): ActionMenuItem {
    const pass = i.answers.filter(answer => answer.answer === 'No').length === 0;
    return new ActionMenuItem(
      i.id,
      pass ? 'check_circle' : 'cancel',
      i.formName ?? (onStart ? 'Pre-Trip' : 'Post-Trip'),
      null,
      null,
      '',
      pass ? 'green' : 'red',
      () => false,
      null,
      null,
      () => this.previewDVIR(onStart),
      null,
      null,
      []
    );
  }

  hasFeatureFlag(featureFlag: string): boolean {
    return this.configuration.featureFlags.find(value => value.isEnabled && value.name === featureFlag) !== undefined;
  }

  /**
   * Scrolls to the observation with the given ID and selects it.
   * @param id - The ID of the observation to scroll to and select.
   */
  selectAndScrollObservation(id: number) {
    const elements = this.itemRefs();
    const targetElement = elements.find(el => el.nativeElement?.getAttribute('id') === `obs-${id}`);
    if (targetElement) {
      targetElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

}
